/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './LobbyKiosk.scss';
import { elexity } from '../utilities/LobbyKioskGraphHelper';
import configuration from '../config';
import AuthenticatedComponent from '../utilities/AuthenticatedComponent';
import WithRouter from '../utilities/WithRouter';

import { useCampusData, useCampusMeta } from '../context/CampusContextProvider';

import KioskModel from '../components/KioskModel';
import {
  kioskCards,
  energyCards,
  co2Cards,
  noModelCards,
  footerCards,
} from '../utilities/LobbyKioskCardHelper';

import elexity_logo from '../assets/elexity_logo_full.svg';
import elexity_logo_dark from '../assets/elexity_logo_full_dark.svg';

import { setCurrentGX, setCurrentGXData } from '../actions';
import { connect } from 'react-redux';

require('highcharts/modules/accessibility');
require('highcharts/modules/draggable-points');
require('highcharts/modules/exporting');
require('highcharts/modules/export-data');

const LobbyKiosk = ({ currentGXData, currentGXId, setCurrentGX, updateGXData }) => {
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const [newGraphConfiguration, setNewGraphConfiguration] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const { data: campusData } = useCampusData();
  const campusMeta = useCampusMeta();

  const mode = searchParams.get('mode');
  const version = searchParams.get('version');
  const liveChartRef = useRef(null);
  const authenticatedComponent = new AuthenticatedComponent();

  if (currentGXData && currentGXData.kiosk && energyCards.length === 0) {
    kioskCards(currentGXData, mode === 'dark');
  }

  //Load initial graphs
  let graphConfiguration = {
    seriesDefinitions: [
      {
        name: 'Grid Power',
        color: '#FA974F',
        type: 'spline',
        opacity: 1,
        dataGrouping: { approximation: 'average' },
      },
      {
        name: 'Total Load',
        color: '#2FE0E6',
        type: 'column',
        opacity: 0.75,
        dataGrouping: { approximation: 'average' },
      },
    ],

    defaultLiveZoom: 3,
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let tempGX = window.location.pathname.split('/')[3];
    setCurrentGX(window.location.pathname.split('/')[3]);
    updateGXData();

    elexity.admin.monitor.graphs.initAuth(authenticatedComponent.generateAuthenticatedHeader());
    elexity.admin.monitor.graphs.init(
      tempGX,
      configuration['backend_host'] + '/ahiapi/lobbyKiosk/' + tempGX + '/graph/definition',
      configuration['backend_host'] + '/ahiapi/lobbyKiosk/' + tempGX + '/graph',
      graphConfiguration,
      'livePerformanceGraph'
    );
  }, []);

  useEffect(() => {
    localStorage.clear();

    if (searchParams) {
      if (mode === 'dark') {
        localStorage.setItem('theme', 'dark');
        document.documentElement.setAttribute('data-theme', 'dark');
      }

      if (version === 'noModel') {
        localStorage.setItem('version', 'noModel');
        document.documentElement.setAttribute('data-version', 'noModel');
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (newGraphConfiguration !== null && newGraphConfiguration.seriesDefinitions) {
      elexity.admin.monitor.graphs.initAuth(authenticatedComponent.generateAuthenticatedHeader());
      elexity.admin.monitor.graphs.init(
        currentGXId,
        configuration['backend_host'] + '/ahiapi/lobbyKiosk/' + currentGXId + '/graph/definition',
        configuration['backend_host'] + '/ahiapi/lobbyKiosk/' + currentGXId + '/graph',
        newGraphConfiguration,
        'livePerformanceGraph'
      );
    }
  }, [newGraphConfiguration]);

  useEffect(() => {
    if (currentGXData && currentGXData.kiosk) {
      //Show or hide battery, solar, ev based on whether or not it's on a campus
      if (currentGXData.kiosk && newGraphConfiguration === null) {
        if (currentGXData.kiosk) {
          if (currentGXData.kiosk.hasBattery) {
            addSeriesDefinition(true, 'Battery Power', '#02BE7F');
          }
          if (currentGXData.kiosk.hasSolar) {
            addSeriesDefinition(true, 'Solar Power', '#F5D500');
          }
          if (currentGXData.kiosk.hasEvCharger) {
            addSeriesDefinition(true, 'EV Charger Power', '#0FFDC2');
          }
        }
      }

      //Graph data update every 5 seconds
      let interval = setInterval(() => {
        updateGXData();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [currentGXData]);

  function addSeriesDefinition(available, name, color) {
    setNewGraphConfiguration(
      { ...graphConfiguration },
      graphConfiguration.seriesDefinitions.splice(1, 0, {
        name: name,
        color: color,
        type: 'spline',
        opacity: 1,
        dataGrouping: { approximation: 'average' },
        visible: available,
      })
    );
  }

  const renderBrandingBlock = () => {
    if (!currentGXData || !currentGXData?.logoUrl) {
      return <></>;
    }
    return (
      <section className="branding-block">
        <div className="branding-container">
          <img src={currentGXData.logoUrl} alt="" />
        </div>
      </section>
    );
  };

  const renderNowBlock = () => {
    if (!currentGXData || !currentGXData?.logoUrl) {
      return <></>;
    }
    return (
      <section className="now-block">
        <div className="now-container">
          {campusMeta?.timezone && (
            <>
              <div className="date">
                <span style={{ display: 'block', margin: '0 0 4px' }}>
                  {currentTime.toLocaleDateString([], {
                    weekday: 'long',
                  })}
                </span>
                <span>
                  {currentTime.toLocaleDateString([], {
                    // weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
              <div className="time">
                {currentTime.toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZone: campusMeta.timezone,
                })}
              </div>
            </>
          )}
          {campusData?.temperature && (
            <div className="temperature">
              <span className="outside-temp-label">Outside Temperature</span>
              {campusData?.temperature} °{campusData?.campusDescription?.temperatureUnit}
            </div>
          )}
        </div>
      </section>
    );
  };

  console.log('co2Cards', co2Cards);

  return (
    <>
      <div
        className={
          mode === 'dark' ? 'lobby-kiosk-page dark lobby-kiosk' : 'lobby-kiosk-page lobby-kiosk'
        }
      >
        <div className="top-data">
          <div className="meta">
            {renderBrandingBlock()}
            {renderNowBlock()}
          </div>
          <section className="kiosk-stats content-block">
            <div className="stats-container">
              {/* <h4>30-Day Climate Savings from Solar and Energy Management</h4> */}
              <h4>Last 30 Days</h4>
              <div className="data-points">
                {co2Cards}
                {energyCards}
              </div>
              {/* {version !== 'noModel' ? (
                <>
                  <div className="energy-stat-container">
                    <h6>Energy</h6>
                    {energyCards}
                  </div>

                  <hr />

                  <div className="co2-stat-container">
                    <h6>
                      CO<sup>2</sup> Equivalencies
                    </h6>
                    {co2Cards}
                  </div>
                </>
              ) : (
                <>
                  <div className="co2-stat-container">{noModelCards}</div>
                </>
              )} */}
            </div>
          </section>
        </div>
        <section className="graph-kiosk">
          {version !== 'noModel' && (
            <div className="kiosk-model--container content-block">
              <h4>Now</h4>
              {currentGXData && currentGXData.kiosk && (
                <KioskModel
                  gxPower={currentGXData.kiosk.gridkW}
                  hasSolar={currentGXData.kiosk.hasSolar}
                  solarPower={currentGXData.kiosk.solarkW}
                  hasBattery={currentGXData.kiosk.hasBattery}
                  batterySoc={currentGXData.kiosk.batterySoc}
                  batterySockWh={currentGXData.kiosk.batterySockWh}
                  batteryDisplaySoc={currentGXData.kiosk.batteryDisplaySoc}
                  batteryPower={currentGXData.kiosk.batterykW}
                  hasEv={currentGXData.kiosk.hasEvCharger}
                  hasEvConnected={currentGXData.kiosk.hasEvConnected}
                  evPower={currentGXData.kiosk.evChargerkW}
                  hvacPower={currentGXData.kiosk.hvackW}
                  otherPower={currentGXData.kiosk.buildingkW}
                />
              )}
            </div>
          )}

          <div
            className={
              version === 'noModel'
                ? 'energy-insights-graph content-block full-width'
                : 'energy-insights-graph content-block'
            }
          >
            <h4>Energy Insights - last 3 days</h4>
            <div
              id="livePerformanceGraph"
              className="insights-graph-container"
              ref={liveChartRef}
            ></div>
          </div>
        </section>

        <section className="lobby-footer">
          {version === 'noModel' && (
            <>
              <div className="footer-stats">{footerCards}</div>
            </>
          )}
          <div className="elexity-logo">
            <p>Powered By</p>
            <img src={mode === 'dark' ? elexity_logo_dark : elexity_logo} alt="" />
          </div>
        </section>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    campuses: state.campuses,
    currentCampusId: state.currentCampusId,
    currentGXId: state.currentGXId,
    currentCampusData: state.currentCampusData,
    currentGXData: state.currentGXData,
  };
};

// used by parent class AuthenticatedComponent
const mapDispatchToProps = (dispatch) => {
  return {
    setCurrentGX: (gxId) => {
      dispatch(setCurrentGX(gxId));
    },
    setCurrentGXData: (gxData) => {
      dispatch(setCurrentGXData(gxData));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithRouter(LobbyKiosk));
